import React, {useRef} from 'react'
import {Box, mergeRefs} from '@chakra-ui/react3'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useGesture} from '@use-gesture/react'
import useZoomGestures from '../../../hooks/use-zoom-gestures'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

const ZOOM_SCALE = 1.5
const ZOOM_IN_CURSOR = getAssetUrl('static/ico/zoom-in-cursor.svg')
const ZOOM_OUT_CURSOR = getAssetUrl('static/ico/zoom-out-cursor.svg')

const ZoomImage = React.forwardRef(({enabled = true, children, ...props}, ref) => {
    const {formatMessage} = useIntl()
    const containerRef = useRef(null)
    const zoomableRef = useRef(null)

    const {state, gestureHandlers, gestureConfig} = useZoomGestures({
        enabled,
        maxScale: ZOOM_SCALE,
        zoomableElementRef: zoomableRef,
        containerRef
    })

    useGesture(gestureHandlers, gestureConfig)

    const mergedRef = mergeRefs(ref, containerRef)

    const stopIfZoomed = (e) => {
        if (state.isZoomed) e.stopPropagation()
    }

    const cursor = enabled
        ? `url("${state.isZoomed ? ZOOM_OUT_CURSOR : ZOOM_IN_CURSOR}") 18 18, ${
              state.isZoomed ? 'zoom-out' : 'zoom-in'
          }`
        : undefined

    return (
        <Box
            ref={mergedRef}
            overflow="hidden"
            position="relative"
            cursor={cursor}
            onTouchStart={(e) => {
                props.onTouchStart?.(e)
                stopIfZoomed(e)
            }}
            onTouchMove={(e) => {
                props.onTouchMove?.(e)
                stopIfZoomed(e)
            }}
            onTouchEnd={(e) => {
                props.onTouchEnd?.(e)
                stopIfZoomed(e)
            }}
            role={enabled ? 'button' : undefined}
            aria-label={
                enabled
                    ? state.isZoomed
                        ? formatMessage({
                              id: 'leap.zoom_image.assistive_msg.zoom_out',
                              defaultMessage: 'Zoom out'
                          })
                        : formatMessage({
                              id: ' leap.zoom_image.assistive_msg.zoom_in',
                              defaultMessage: 'Zoom in'
                          })
                    : undefined
            }
            {...props}
        >
            <Box
                ref={zoomableRef}
                style={{
                    transform: enabled ? state.transform : undefined,
                    touchAction: enabled ? (state.isZoomed ? 'none' : 'pan-x pan-y') : undefined,
                    transition: 'transform 220ms ease'
                }}
            >
                {children}
            </Box>
        </Box>
    )
})

if (process.env.NODE_ENV !== 'production') {
    ZoomImage.displayName = 'ZoomImage'
}

ZoomImage.propTypes = {
    enabled: PropTypes.bool,
    children: PropTypes.node,
    onTouchStart: PropTypes.func,
    onTouchMove: PropTypes.func,
    onTouchEnd: PropTypes.func
}

export default ZoomImage
